import React from "react";
import axios from "axios";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QRType } from "@/types/qr";
import { logError, logWarning, logInfo } from "@/services/DatadogService";

export type ValidationRule =
  | { type: "isEmpty"; getter: () => any[] | null | undefined }
  | { type: "asyncUrlValidation"; getter: () => string | null | undefined; endpoint: string }
  | { type: "custom"; validator: () => boolean | Promise<boolean> };

export interface UsePreviewValidationOptions {
  rules: ValidationRule[];
  dependencies: React.DependencyList;
  context: QREditorContextProps<QRType> | null;
  isInitPreview: boolean;
}

/**
 * Hook to centralize the useEffect + setValidationError pattern
 * Supports different types of validation rules used in previews
 */
export function usePreviewValidation({
  rules,
  dependencies,
  context,
  isInitPreview,
}: UsePreviewValidationOptions) {
  React.useEffect(() => {
    // Only execute validation if not initial view and context exists
    if (isInitPreview || !context?.setValidationError) {
      return;
    }

    const validateRules = async () => {
      try {
        let isValid = true;

        for (const rule of rules) {
          switch (rule.type) {
            case "isEmpty": {
              const array = rule.getter();
              if (!array || array.length === 0) {
                isValid = false;
                break;
              }
              break;
            }

            case "asyncUrlValidation": {
              const url = rule.getter();
              if (!url) {
                isValid = false;
                break;
              }

              try {
                await axios.get(rule.endpoint.replace("{url}", encodeURIComponent(url)));
                // If we reach here, the URL is valid
              } catch (error) {
                logWarning("Async URL validation failed during preview validation", {
                  validationRule: "asyncUrlValidation",
                  url,
                  endpoint: rule.endpoint,
                  previewComponent: "usePreviewValidation",
                  error: error instanceof Error ? error.message : String(error),
                });
                isValid = false;
                break;
              }
              break;
            }

            case "custom": {
              try {
                const result = await Promise.resolve(rule.validator());
                if (!result) {
                  isValid = false;
                  break;
                }
              } catch (error) {
                logError(error instanceof Error ? error : new Error(String(error)), {
                  validationRule: "custom",
                  previewComponent: "usePreviewValidation",
                });
                isValid = false;
                break;
              }
              break;
            }
          }

          // If a rule fails, we don't need to continue
          if (!isValid) break;
        }

        context.setValidationError(!isValid);

        // Log only in development for tracking successful validations
        if (process.env.NODE_ENV === "development" && isValid && rules.length > 0) {
          logInfo("Preview validation completed successfully", {
            rulesCount: rules.length,
            context: "preview-validation",
          });
        }
      } catch (error) {
        // In case of general error, mark as invalid
        logError(error instanceof Error ? error : new Error(String(error)), {
          previewComponent: "usePreviewValidation",
          rulesCount: rules.length,
          phase: "validation-execution",
        });
        context.setValidationError(true);
      }
    };

    validateRules();
  }, dependencies);
}

/**
 * Helper to create common validation rules
 */
export const createValidationRules = {
  /**
   * Validates that an array is not empty (for apps, images, social)
   */
  arrayNotEmpty: (getter: () => any[]): ValidationRule => ({
    type: "isEmpty",
    getter,
  }),

  /**
   * Async URL validation using oembed (for YouTube)
   */
  youtubeUrl: (getter: () => string | undefined): ValidationRule => ({
    type: "asyncUrlValidation",
    getter,
    endpoint: "https://www.youtube.com/oembed?url={url}",
  }),

  /**
   * Validador personalizado
   */
  custom: (validator: () => boolean | Promise<boolean>): ValidationRule => ({
    type: "custom",
    validator,
  }),
};
